import React, { useState, useEffect } from 'react';
import { Play, Pause, RefreshCw, Clock, CheckCircle, XCircle, Loader2 } from 'lucide-react';
import { syncAPI, type SyncJobStatus } from '@/api/sync';
import { Button } from './Button';

export const SyncControl: React.FC = () => {
  const [jobStatus, setJobStatus] = useState<SyncJobStatus | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isControlling, setIsControlling] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const fetchStatus = async () => {
    try {
      setIsLoading(true);
      const response = await syncAPI.getJobStatus();
      setJobStatus(response.data.data);
    } catch (error: any) {
      console.error('Failed to fetch sync status:', error);
      setMessage({ type: 'error', text: 'فشل في تحميل حالة المزامنة' });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchStatus();
    // Refresh status every 30 seconds
    const interval = setInterval(fetchStatus, 30000);
    return () => clearInterval(interval);
  }, []);

  const handleStopSync = async () => {
    try {
      setIsControlling(true);
      setMessage(null);
      await syncAPI.stopSync();
      setMessage({ type: 'success', text: 'تم إيقاف المزامنة التلقائية بنجاح' });
      await fetchStatus();
    } catch (error: any) {
      console.error('Failed to stop sync:', error);
      setMessage({ type: 'error', text: error.response?.data?.error?.message || 'فشل إيقاف المزامنة' });
    } finally {
      setIsControlling(false);
    }
  };

  const handleStartSync = async () => {
    try {
      setIsControlling(true);
      setMessage(null);
      await syncAPI.startSync();
      setMessage({ type: 'success', text: 'تم تشغيل المزامنة التلقائية بنجاح' });
      await fetchStatus();
    } catch (error: any) {
      console.error('Failed to start sync:', error);
      setMessage({ type: 'error', text: error.response?.data?.error?.message || 'فشل تشغيل المزامنة' });
    } finally {
      setIsControlling(false);
    }
  };

  const handleManualSync = async () => {
    try {
      setIsSyncing(true);
      setMessage(null);
      await syncAPI.triggerSync();
      setMessage({ type: 'success', text: 'تم بدء المزامنة اليدوية بنجاح' });
      // Sync is async, so we just show the message
    } catch (error: any) {
      console.error('Failed to trigger manual sync:', error);
      setMessage({ type: 'error', text: error.response?.data?.error?.message || 'فشل بدء المزامنة اليدوية' });
    } finally {
      setIsSyncing(false);
    }
  };

  const handleManualCarSync = async () => {
    try {
      setIsSyncing(true);
      setMessage(null);
      await syncAPI.triggerCarSync();
      setMessage({ type: 'success', text: 'تم بدء مزامنة السيارات بنجاح' });
    } catch (error: any) {
      console.error('Failed to trigger car sync:', error);
      setMessage({ type: 'error', text: error.response?.data?.error?.message || 'فشل بدء مزامنة السيارات' });
    } finally {
      setIsSyncing(false);
    }
  };

  if (isLoading && !jobStatus) {
    return (
      <div className="card">
        <div className="flex items-center justify-center py-8">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </div>
    );
  }

  return (
    <div className="card">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-white flex items-center gap-2">
          <RefreshCw className="h-5 w-5 text-primary" />
          التحكم في المزامنة التلقائية
        </h3>
        <Button
          variant="secondary"
          size="sm"
          onClick={fetchStatus}
          isLoading={isLoading}
          className="text-gray-400 hover:text-white"
        >
          <RefreshCw className="h-4 w-4" />
        </Button>
      </div>

      {/* Message Display */}
      {message && (
        <div className={`mb-4 p-3 rounded-lg flex items-center gap-2 ${
          message.type === 'success' 
            ? 'bg-success/10 text-success border border-success/20' 
            : 'bg-danger/10 text-danger border border-danger/20'
        }`}>
          {message.type === 'success' ? (
            <CheckCircle className="h-5 w-5" />
          ) : (
            <XCircle className="h-5 w-5" />
          )}
          <span>{message.text}</span>
        </div>
      )}

      {/* Sync Job Status */}
      {jobStatus && (
        <div className="space-y-4">
          {/* Battery Sync Status */}
          <div className="p-4 bg-white/5 rounded-lg">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <div className={`w-3 h-3 rounded-full ${
                  jobStatus.syncJob.running ? 'bg-success animate-pulse' : 'bg-gray-500'
                }`} />
                <span className="font-medium text-white">
                  مزامنة البطاريات
                </span>
              </div>
              <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                jobStatus.syncJob.running 
                  ? 'bg-success/20 text-success' 
                  : 'bg-gray-500/20 text-gray-400'
              }`}>
                {jobStatus.syncJob.running ? 'نشطة' : 'متوقفة'}
              </span>
            </div>

            {jobStatus.syncJob.running && (
              <div className="flex items-center gap-2 text-sm text-gray-400 mb-3">
                <Clock className="h-4 w-4" />
                <span>الجدول: {jobStatus.syncJob.schedule} (كل 5 دقائق)</span>
              </div>
            )}

            <div className="flex gap-2">
              {jobStatus.syncJob.running ? (
                <Button
                  variant="danger"
                  size="sm"
                  onClick={handleStopSync}
                  isLoading={isControlling}
                  disabled={isControlling}
                  className="flex-1"
                >
                  <Pause className="h-4 w-4" />
                  إيقاف المزامنة التلقائية
                </Button>
              ) : (
                <Button
                  variant="primary"
                  size="sm"
                  onClick={handleStartSync}
                  isLoading={isControlling}
                  disabled={isControlling}
                  className="flex-1"
                >
                  <Play className="h-4 w-4" />
                  تشغيل المزامنة التلقائية
                </Button>
              )}
            </div>
          </div>

          {/* Manual Sync Options */}
          <div className="p-4 bg-white/5 rounded-lg">
            <h4 className="font-medium text-white mb-3">المزامنة اليدوية</h4>
            <div className="space-y-2">
              <Button
                variant="primary"
                size="sm"
                onClick={handleManualSync}
                isLoading={isSyncing}
                disabled={isSyncing}
                className="w-full"
              >
                <RefreshCw className="h-4 w-4" />
                مزامنة بيانات البطاريات الآن
              </Button>
              <Button
                variant="secondary"
                size="sm"
                onClick={handleManualCarSync}
                isLoading={isSyncing}
                disabled={isSyncing}
                className="w-full"
              >
                <RefreshCw className="h-4 w-4" />
                مزامنة بيانات السيارات الآن
              </Button>
            </div>
          </div>

          {/* Info */}
          <div className="p-3 bg-info/10 border border-info/20 rounded-lg">
            <p className="text-sm text-info">
              💡 المزامنة التلقائية تقوم بتحديث البيانات من Google Sheets كل 5 دقائق. 
              يمكنك إيقافها مؤقتاً أثناء إجراء تعديلات يدوية على النظام.
            </p>
          </div>
        </div>
      )}
    </div>
  );
};
